/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {TABLE_FILTER_IDS} from '@agent-assist/common/lib/admin/table-filters';
import {Box} from '@mui/material';
import type {GridColDef, GridFilterModel} from '@mui/x-data-grid-premium';
import {useEffect, useState, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {ContactModal} from '../components/ContactModal';
import ImportsSelect from '../components/ImportsSelect';
import {PageContainer} from '../components/PageContainer';
import {type DateRangeValue, SuperDatePicker} from '../components/SuperTable/SuperDatePicker';
import SuperTable from '../components/SuperTable/SuperTable';
import {useGetContactsFiltered} from '../hooks/abstractions/useGetContactsFiltered';
import {useDynamicHeight} from '../hooks/useDynamicHeight';
import {useSuperTableOptions} from '../hooks/useSuperTableOptions';
import {ROUTES} from '../routes';

export const CalibrationPage = () => {
  const [modalContactId, setModalContactId] = useState<string>();
  const [contactImportId, setContactImportId] = useState<string | undefined>();
  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const [dateRange, setDateRange] = useState<DateRangeValue>([null, null]);
  const [options, updateOptions] = useSuperTableOptions(TABLE_FILTER_IDS.CALIBRATION_TABLE);
  const {get, isError, isLoading, isSuccess} = useGetContactsFiltered();
  const superTableContainerRef = useRef<HTMLDivElement>(null);
  const {height: tableHeight, updateHeight} = useDynamicHeight(superTableContainerRef);
  const navigate = useNavigate();

  useEffect(() => {
    if (contactImportId) {
      const filterModelIsEmpty = !filterModel?.items?.length;
      const effectiveFilter =
        filterModelIsEmpty && options.reapplyFilter && options.lastFilter ? options.lastFilter : filterModel;

      const getContacts = async () => {
        try {
          const data = await get({
            contactImportId,
            filterModel: effectiveFilter,
            dateRange,
            pageSize: options.rowsLimit,
          });
          setRows(data.rows);
          setColumns(data.columns);
          updateHeight();
        } catch (e) {
          console.error(e);
        }
      };
      void getContacts();
    }
  }, [contactImportId, filterModel, dateRange, options.rowsLimit]);

  const onCloseContactModal = () => {
    setModalContactId(undefined);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseContactModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleContactImportIdChanged = (id: string) => {
    setContactImportId(id);
  };

  const handleRowClick = (params: any, prefersModal: boolean) => {
    if (prefersModal) {
      setModalContactId(params.id);
    } else {
      if (contactImportId) {
        navigate(ROUTES.CONTACT_VIEW(contactImportId, params.id));
      }
    }
  };

  const handleApplyFilter = (newFilterModel: GridFilterModel | undefined) => {
    setFilterModel(newFilterModel);
  };

  const handleDateRangeChange = (newDateRange: DateRangeValue) => {
    setDateRange(newDateRange);
  };

  const actions = (
    <>
      <SuperDatePicker onDateRangeSelected={handleDateRangeChange} />
      <ImportsSelect onSelect={handleContactImportIdChanged} />
    </>
  );

  return (
    <PageContainer title={'Calibration'} actions={actions}>
      <Box ref={superTableContainerRef}>
        <SuperTable
          columns={columns}
          rows={rows}
          onRowClick={handleRowClick}
          onApplyFilter={handleApplyFilter}
          noRowsLabel={!isSuccess ? 'Select an Import to begin.' : undefined}
          isLoading={isLoading}
          isError={isError}
          height={tableHeight ? `${tableHeight}px` : undefined}
          tableId={TABLE_FILTER_IDS.CALIBRATION_TABLE}
          options={options}
          updateOptions={updateOptions}
        />
      </Box>
      {!!contactImportId && !!modalContactId && (
        <ContactModal
          contactImportId={contactImportId}
          contactId={modalContactId}
          closeContactModal={onCloseContactModal}
          calibrationMode
        />
      )}
    </PageContainer>
  );
};
